import React from 'react'
import {useLocation} from 'react-router-dom'
import {Helmet} from 'react-helmet'

// OOTB utils
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'

// Custom Utils
import {getAppDomain} from '../../utils/utils'

// Custom hooks
import useMultiSite from '../../hooks/use-multi-site'

// Third Party
import PropTypes from 'prop-types'

// constants
import {HOME_HREF} from '../../constants'

/**
 * SeoLinks is a React component that generates and manages SEO-related link elements
 * for alternate languages and canonical URLs. It utilizes the Helmet library to
 * dynamically insert these links into the document head, ensuring that search engines
 * can properly index and understand the language and regional variations of the site.
 *
 * @param {Object} props - The component properties.
 * @param {string} props.masterProductId - The master product ID used to determine if the
 *                                         current page is a Product Detail Page (PDP).
 *
 * @returns {JSX.Element} A JSX element containing Helmet components with alternate and
 *                        canonical link elements.
 *
 * The component uses several hooks and utilities:
 * - `useLocation`: Retrieves the current location object from the router.
 * - `useMultiSite`: Provides site and locale information for multi-site configurations.
 * - `useAppOrigin`: Retrieves the origin of the application.
 * - `getConfig`: Fetches the application configuration, including site and locale details.
 *
 * The component logic includes:
 * - Determining if the current page is a PDP or the home page.
 * - Building canonical URLs based on the current path, site, and locale.
 * - Generating alternate link elements for each supported locale of each site, excluding
 *   sites with 'OUTLET' in their ID.
 * - Adding a default alternate link with `hrefLang="x-default"` for the home page or
 *   current path.
 * - Using the `useEffect` hook to update alternate links when dependencies change.
 *
 * The component conditionally renders Helmet components based on whether the code is
 * executed on the server or client, ensuring proper SEO link management in both
 * environments.
 */
const SeoLinks = ({masterProductId}) => {
    const location = useLocation()
    const {site, locale} = useMultiSite()
    const appOrigin = getAppDomain()
    const {app: appConfig} = getConfig()
    const currentPath = location.pathname
    const isPDP = Boolean(masterProductId)
    const hasNoPath = currentPath === '/'

    const buildCanonicalUrl = (siteAlias, localeAlias) => {
        const pathWithoutSiteAndLocale = currentPath
            .replace(new RegExp(`^/${site.alias}`), '')
            .replace(new RegExp(`^/${locale.alias}`), '')

        const finalPath = isPDP
            ? `/product/${masterProductId}`
            : hasNoPath
            ? HOME_HREF
            : pathWithoutSiteAndLocale

        return `${appOrigin}/${siteAlias}/${localeAlias}${finalPath}`
    }

    return (
        <Helmet>
            {appConfig.sites
                .filter((site) => !site.id.includes('OUTLET'))
                .flatMap((site) =>
                    site?.l10n?.supportedLocales.map((locale) => {
                        const hrefLang = locale.id.toLowerCase().endsWith('us')
                            ? locale.id.toLowerCase()
                            : locale.alias.toLowerCase()
                        const href = buildCanonicalUrl(appConfig.siteAliases[site.id], locale.alias)

                        return (
                            <link
                                rel="alternate"
                                hrefLang={hrefLang}
                                href={href}
                                key={`${site.id}-${locale.id}`}
                            />
                        )
                    })
                )}

            {/* has no path -> The dispatch page's URL does not cater to any specific locale, it points to itself */}
            {/* has path -> VRAM-2333: The default should point to the current page on /eu/en */}
            <link
                rel="alternate"
                hrefLang="x-default"
                href={hasNoPath ? appOrigin : buildCanonicalUrl('eu', 'en')}
            />

            {isPDP && (
                <link
                    rel="canonical"
                    href={buildCanonicalUrl(appConfig.siteAliases[site.id], locale.alias)}
                />
            )}
        </Helmet>
    )
}

SeoLinks.displayName = 'SeoLinks'

SeoLinks.propTypes = {
    masterProductId: PropTypes.string
}

export default SeoLinks
